/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useState, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { isGroupReportLessonType } from "../common/lessonTypes";
import { sortAttendanceData, SORT_DIRECTION } from "./helpers/AttendanceSortHelpers";
import { filterAttendanceData, shouldFilterOutStudent, shouldFilterOutGroup } from "./helpers/AttendanceFilterHelpers";
import { toggleRowExpansion, areAllRowsExpanded, toggleAllRows } from "./helpers/AttendanceExpandHelpers";
import AttendanceReportPaymentModal from "./AttendanceReportPaymentModal";
import TableHeader from "./TableHeader";
import FilterBar from "./FilterBar";
import GroupRow from "./GroupRow";
import StudentRow from "./StudentRow";
import Pagination from "./Pagination";
import { useTranslation } from 'react-i18next';

const AttendanceList = ({ userId, report, attendance, onEdit, lessonTypes }) => {
  const { t: tr } = useTranslation();
  const NUMBER_TO_SHOW = 250;
  const [expandedRows, setExpandedRows] = useState([]);
  const [expandedRowsToggle, setExpandedRowsToggle] = useState(true);
  const uniqueClassTypes = [...new Set(attendance.map(reg => reg.classType))];
  const [selectedRows, setSelectedRows] = useState({});
  const [showFilters, setShowFilters] = useState(false);

  const [filters, setFilters] = useState({
    date: "",
    dayOfWeek: "",
    startTime: "",
    classType: "",
    studentName: "",
    clientName: "",
    teacherName: "",
    isPresent: false,
    comment: "",
  });

  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: SORT_DIRECTION.ASCENDING,
  });
  const [numToShow, setNumToShow] = useState(NUMBER_TO_SHOW);

  const sortedAttendance = useMemo(() => sortAttendanceData(attendance, sortConfig, lessonTypes),
    [attendance, sortConfig, lessonTypes]);

  const filteredAttendance = useMemo(() => filterAttendanceData(sortedAttendance, filters), [sortedAttendance, filters]);

  const toggleExpandRow = (groupId) => {
    const newExpandedRows = toggleRowExpansion(groupId, expandedRows, filteredAttendance);
    setExpandedRows(newExpandedRows);

    const allRowsExpanded = areAllRowsExpanded(newExpandedRows, filteredAttendance, lessonTypes);
    setExpandedRowsToggle(!allRowsExpanded);
  };

  const expandAllRows = () => {
    setExpandedRowsToggle((prevToggleState) => {
      const { expandedRows: newExpandedRows, expandedRowsToggle: newToggleState } =
        toggleAllRows(prevToggleState, filteredAttendance, lessonTypes);

      setExpandedRows(newExpandedRows);
      return newToggleState;
    });
  };

  useEffect(() => {
    expandAllRows();
  }, []);

  const handleSort = (key) => {
    let direction = SORT_DIRECTION.ASCENDING;
    if (sortConfig && sortConfig.key === key) {
      direction =
        sortConfig.direction === SORT_DIRECTION.ASCENDING ? SORT_DIRECTION.DESCENDING : SORT_DIRECTION.ASCENDING;
    }
    setSortConfig({ key, direction });
  };


  const handleStudentSelection = (groupId, studentId, isChecked) => {
    setSelectedRows(prevSelectedRows => ({
      ...prevSelectedRows,
      [groupId]: {
        ...prevSelectedRows[groupId],
        [studentId]: isChecked,
      },
    }));
  };

  const isGroupSelected = (groupId, students) => {
    const selectedStudents = students.filter(student => (selectedRows[groupId] ? selectedRows[groupId][student.id] === true : false));
    const filteredOutStudents = students.filter(student => shouldFilterOutStudent(student, filters));
    const totalStudents = students.length;
    if (selectedStudents.length === totalStudents) {
      return true;
    }
    return selectedStudents.length + filteredOutStudents.length ===
      totalStudents;
  };

  const calculateSelectedRows = selectionType => filteredAttendance
    .map((reg) => {
      const selectedStudents = reg.students.filter((student) => {
        const isSelected = selectedRows[reg.id]
          ? selectedRows[reg.id][student.id]
          : false;
        const filterOutStudent = shouldFilterOutStudent(student, filters);
        return isSelected && !filterOutStudent;
      });

      let updatedStudents;

      if (selectionType === "client") {
        updatedStudents = selectedStudents;
      } else if (selectedStudents.length > 0) {
        updatedStudents = reg.students;
      } else {
        updatedStudents = [];
      }

      return {
        ...reg,
        students: updatedStudents,
      };
    })
    .filter(reg => reg.students.length > 0);

  const handleShowMore = () => {
    setNumToShow(prev => prev + 10);
  };

  const handleShowAll = () => {
    setNumToShow(filteredAttendance.length);
  };

  return (
    <div className="attendance-report-list">
      <div className="row d-flex mx-0 mb-3 align-items-center">
        <div className="col-xl-7 manage-title pr-3">
          <div className="row">{tr("billing:attendanceReport_77")}</div>
          <div className="row manage-subtitle">
            {report.clientId === "all" ||
            (!report.clientId && report.teacherId === "all")
              ? tr("manage:extraAllClients_95")
              : report.clientName}{" "}
            {" - "}
            {report.teacherId === "all" ||
            (!report.teacherId && report.clientId === "all")
              ? tr("manage:extraAllTeachers_96")
              : `${report.teacherName}`}{" "}
            - {moment(report.startDate).format("DD.MM.YYYY")} -{" "}
            {moment(report.endDate).format("DD.MM.YYYY")}
          </div>
        </div>
        <div className="col-xl-5 ml-auto">

          <div className="row">
            {userId === "marciniak.alicja@gmail.com" && (
            <>
              <div className="col-xl p-0 pr-xl-2">
                <AttendanceReportPaymentModal
                  selected={calculateSelectedRows("client")}
                  forClient
                  lessonTypes={lessonTypes}
                />
              </div>
              <div className="col-xl p-0 pr-xl-2">
                <AttendanceReportPaymentModal
                  selected={calculateSelectedRows("teacher")}
                  forClient={false}
                  lessonTypes={lessonTypes}
                />
              </div>
            </>
            )}
            <div className="col-xl p-0">
              <button
                type="button"
                className="btn manage-button btn-success btn-block h-100"
                onClick={onEdit}
              >{tr("billing:changeReport_78")}</button>
            </div>
          </div>
        </div>
      </div>

      <button
        type="button"
        className="btn manage-button manage-teal-button d-md-none mb-3"
        aria-expanded={showFilters}
        aria-controls="attendance-report-filters"
        onClick={() => setShowFilters(value => !value)}
      >{tr('manage:reportFilters')}
      </button>
      <div id="attendance-report-filters" className={showFilters ? '' : 'd-none d-md-block'}>
        <FilterBar
          filters={filters}
          setFilters={setFilters}
          uniqueClassTypes={uniqueClassTypes}
          expandedRowsToggle={expandedRowsToggle}
          expandAllRows={expandAllRows}
          setSelectedRows={setSelectedRows}
          filteredAttendance={filteredAttendance}
          lessonTypes={lessonTypes}
        />

      </div>
      <label className="d-block d-md-none" htmlFor="attendance-report-sort">
        {tr('manage:reportSort')}
        <select id="attendance-report-sort" className="form-control" value={sortConfig.key || ''} onChange={event => handleSort(event.target.value)}>
          <option value="">{tr('manage:none_279')}</option>
          {[
            ['date', 'manage:date_8'], ['dayOfWeek', 'billing:weekday_131'], ['startTime', 'calendar:time'],
            ['classType', 'manage:lessonType_196'], ['studentName', 'manage:student_14'], ['clientName', 'manage:client_108'],
            ['teacherName', 'manage:teacher_226'], ['isPresent', 'client:present_31'], ['comment', 'manage:notes_10'],
          ].map(([key, label]) => <option key={key} value={key}>{tr(label)}</option>)}
        </select>
        {sortConfig.key && (
          <button type="button" className="btn manage-teal-button mt-2" onClick={() => handleSort(sortConfig.key)}>
            {tr(sortConfig.direction === SORT_DIRECTION.ASCENDING ? 'manage:sortAscending' : 'manage:sortDescending')}
          </button>
        )}
      </label>
      <div className="overflow-hidden">
        <TableHeader sortConfig={sortConfig} handleSort={handleSort} />
        <div className="">
          {filteredAttendance.slice(0, numToShow).map((reg, index) => {
            const groupShouldBeFilteredOut = shouldFilterOutGroup(reg, filters);
            if (groupShouldBeFilteredOut) return null;


            return (
              <div key={`${reg.id}`} className="attendance-report-lesson">
                <GroupRow
                  reg={reg}
                  index={index}
                  expandedRows={expandedRows}
                  toggleExpandRow={toggleExpandRow}
                  isGroupSelected={isGroupSelected}
                  setSelectedRows={setSelectedRows}
                  lessonTypes={lessonTypes}
                />
                {isGroupReportLessonType(reg.classType, lessonTypes) && (
                  <div
                    className={`collapse-content ${
                      expandedRows.includes(reg.id) ? "expanded" : "collapsed"
                    }`}
                  >
                    {reg.students.map((student) => {
                      const filterOutStudents = shouldFilterOutStudent(
                        student,
                        filters
                      );


                      return (
                        <StudentRow
                          key={`${student.id}`}
                          student={student}
                          reg={reg}
                          index={index}
                          selectedRows={selectedRows}
                          handleStudentSelection={handleStudentSelection}
                          filterOutStudents={filterOutStudents}
                        />
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <Pagination
        numToShow={numToShow}
        attendanceLength={attendance.length}
        handleShowMore={handleShowMore}
        handleShowAll={handleShowAll}
      />
    </div>
  );
};

AttendanceList.propTypes = {
  userId: PropTypes.string.isRequired,
  report: PropTypes.shape({
    year: PropTypes.string,
    month: PropTypes.string,
    teacherName: PropTypes.string,
    clientName: PropTypes.string,
  }).isRequired,
  attendance: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      date: PropTypes.string,
      dayOfWeek: PropTypes.string,
      startTime: PropTypes.string,
      classType: PropTypes.string,
      studentName: PropTypes.string,
      teacherName: PropTypes.string,
      isPresent: PropTypes.bool,
      comment: PropTypes.string,
    })
  ).isRequired,
  onEdit: PropTypes.func.isRequired,
  lessonTypes: PropTypes.shape({
    id: PropTypes.string,
    types: PropTypes.array,
  }).isRequired,
};

export default AttendanceList;
